
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { OnboardingProgress, OrganizationStatus } from '@/types/onboarding';
import { getNextStep } from '@/components/onboarding/config/onboardingSteps';
import { toast } from 'sonner';

export const useOnboarding = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch onboarding progress
  const { 
    data: progress, 
    isLoading: progressLoading, 
    error: progressError,
    refetch: refetchProgress 
  } = useQuery({
    queryKey: ['onboarding-progress', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('user_onboarding_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      return data as OnboardingProgress | null;
    },
    enabled: !!user?.id,
  });

  // Fetch organization status
  const { 
    data: organizationStatus, 
    isLoading: orgLoading 
  } = useQuery({
    queryKey: ['organization-status', user?.id],
    queryFn: async () => {
      if (!user?.id) return { hasOrganization: false };

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profile?.organization_id) {
        const { data: org } = await supabase
          .from('organizations')
          .select('id, name')
          .eq('id', profile.organization_id)
          .single();

        return {
          hasOrganization: true,
          organizationId: org?.id,
          organizationName: org?.name,
          isOwner: true
        };
      }

      return { hasOrganization: false };
    },
    enabled: !!user?.id,
  });

  // Initialize onboarding for new users
  const initializeOnboarding = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_onboarding_progress')
        .insert({
          user_id: user.id,
          is_completed: false,
          completed_steps: [],
          skipped_steps: [],
          current_step: 'welcome',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
    },
    onError: (error) => {
      console.error('Failed to initialize onboarding:', error);
      toast.error('Failed to initialize onboarding');
    },
  });

  // Complete a step
  const completeStep = useMutation({
    mutationFn: async (stepId: string) => {
      if (!user?.id || !progress) throw new Error('Invalid state');

      const nextStep = getNextStep(stepId);
      const isComplete = !nextStep; // No next step means we're done

      const { data, error } = await supabase
        .from('user_onboarding_progress')
        .update({
          completed_steps: [...progress.completed_steps, stepId],
          current_step: nextStep?.id || stepId,
          is_completed: isComplete,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
    },
    onError: (error) => {
      console.error('Failed to complete step:', error);
      toast.error('Failed to complete step');
    },
  });

  // Skip a step
  const skipStep = useMutation({
    mutationFn: async (stepId: string) => {
      if (!user?.id || !progress) throw new Error('Invalid state');

      const nextStep = getNextStep(stepId);
      const isComplete = !nextStep;

      const { data, error } = await supabase
        .from('user_onboarding_progress')
        .update({
          skipped_steps: [...progress.skipped_steps, stepId],
          current_step: nextStep?.id || stepId,
          is_completed: isComplete,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
    },
    onError: (error) => {
      console.error('Failed to skip step:', error);
      toast.error('Failed to skip step');
    },
  });

  // Derived state
  const isLoading = progressLoading || orgLoading;
  const hasErrors = !!progressError;
  const needsOnboarding = !progress && !progressLoading && !progressError;
  const isOnboardingComplete = progress?.is_completed || false;

  return {
    progress,
    organizationStatus: organizationStatus as OrganizationStatus,
    isLoading,
    hasErrors,
    needsOnboarding,
    isOnboardingComplete,
    initializeOnboarding,
    completeStep,
    skipStep,
    refetchProgress,
  };
};
